import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import {
  List, Clock, RefreshCw, Package,
  ChevronLeft, ChevronRight, Search, Filter,
  Edit3, Trash2, Check, X, ArrowUpDown
} from 'lucide-react';
import {
  getSyncPriceLists, updateSyncPriceList, deleteSyncPriceList,
  getSyncShiftSummaries, getSyncLog, getSyncInventoryAdjustments
} from '../../services/api';
import BusinessSearchSelect from '../../components/BusinessSearchSelect';

const TABS = [
  { key: 'price-lists', label: 'Listas de Precios', icon: List },
  { key: 'shifts', label: 'Cierres de Turno', icon: Clock },
  { key: 'sync-log', label: 'Log Sync', icon: RefreshCw },
  { key: 'inventory', label: 'Ajustes Inventario', icon: Package },
];

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const formatMXN = (amount) => {
  if (amount == null) return '\u2014';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

const Pagination = ({ page, pages, total, limit, onPageChange }) => {
  if (total <= limit) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
      <span className="text-xs text-slate-500">{from}-{to} de {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500 px-2 py-1">{page}/{pages}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Badge = ({ label, color }) => (
  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>{label}</span>
);

// ======================== PRICE LISTS TAB ========================

const PriceListsTab = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [businessId, setBusinessId] = useState('');
  const [isActive, setIsActive] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (businessId) params.business_id = businessId;
      if (isActive !== '') params.is_active = isActive === 'true';
      const res = await getSyncPriceLists(params);
      setData(res.data);
    } catch { toast.error('Error cargando listas de precios'); }
    finally { setLoading(false); }
  }, [page, businessId, isActive]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, is_default: item.is_default, is_active: item.is_active });
  };

  const handleSave = async () => {
    try {
      await updateSyncPriceList(editingId, editForm);
      toast.success('Lista actualizada');
      setEditingId(null);
      load();
    } catch { toast.error('Error actualizando'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Desactivar esta lista de precios?')) return;
    try {
      await deleteSyncPriceList(id);
      toast.success('Lista desactivada');
      load();
    } catch { toast.error('Error eliminando'); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-64">
          <BusinessSearchSelect value={businessId} onChange={(id) => { setBusinessId(id); setPage(1); }} />
        </div>
        <select value={isActive} onChange={(e) => { setIsActive(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Todas</option>
          <option value="true">Activas</option>
          <option value="false">Inactivas</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Negocio</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Nombre</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Default</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Activa</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Source</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Fecha</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sin resultados</td></tr>
              ) : data.items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate">{it.business_name}</td>
                  <td className="px-4 py-3">
                    {editingId === it.id ? (
                      <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="border border-slate-200 rounded px-2 py-1 text-sm w-full" />
                    ) : (
                      <span className="font-medium text-slate-900">{it.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editingId === it.id ? (
                      <input type="checkbox" checked={editForm.is_default}
                        onChange={(e) => setEditForm({ ...editForm, is_default: e.target.checked })} />
                    ) : it.is_default ? (
                      <Badge label="Default" color="bg-blue-100 text-blue-700" />
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editingId === it.id ? (
                      <input type="checkbox" checked={editForm.is_active}
                        onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
                    ) : (
                      <Badge label={it.is_active ? 'Activa' : 'Inactiva'}
                        color={it.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{it.source || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(it.created_at)}</td>
                  <td className="px-4 py-3 text-center">
                    {editingId === it.id ? (
                      <div className="flex gap-1 justify-center">
                        <button onClick={handleSave} className="p-1 rounded hover:bg-green-50 text-green-600"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setEditingId(null)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => handleEdit(it)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page || page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </div>
  );
};

// ======================== SHIFT SUMMARIES TAB ========================

const ShiftSummariesTab = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [businessId, setBusinessId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (businessId) params.business_id = businessId;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      const res = await getSyncShiftSummaries(params);
      setData(res.data);
    } catch { toast.error('Error cargando cierres de turno'); }
    finally { setLoading(false); }
  }, [page, businessId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-64">
          <BusinessSearchSelect value={businessId} onChange={(id) => { setBusinessId(id); setPage(1); }} />
        </div>
        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Negocio</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Sucursal</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Cajero</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Apertura</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Cierre</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Ventas</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Total Ventas</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Diferencia</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Fecha Cierre</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Sin resultados</td></tr>
              ) : data.items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{it.business_name}</td>
                  <td className="px-4 py-3 text-slate-600">{it.branch_name}</td>
                  <td className="px-4 py-3 text-slate-600">{it.cashier_name}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{formatMXN(it.opening_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{formatMXN(it.closing_amount)}</td>
                  <td className="px-4 py-3 text-center text-slate-600">{it.sales_count ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{formatMXN(it.sales_total)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-mono ${(it.difference || 0) < 0 ? 'text-red-600' : (it.difference || 0) > 0 ? 'text-green-600' : 'text-slate-500'}`}>
                      {formatMXN(it.difference)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(it.closed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page || page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </div>
  );
};

// ======================== SYNC LOG TAB ========================

const SyncLogTab = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [businessId, setBusinessId] = useState('');
  const [direction, setDirection] = useState('');
  const [productCode, setProductCode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (businessId) params.business_id = businessId;
      if (direction) params.direction = direction;
      if (productCode) params.product_code = productCode;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      const res = await getSyncLog(params);
      setData(res.data);
    } catch { toast.error('Error cargando sync log'); }
    finally { setLoading(false); }
  }, [page, businessId, direction, productCode, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const renderCounts = (counts) => {
    if (!counts || typeof counts !== 'object') return '\u2014';
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ') || '\u2014';
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-64">
          <BusinessSearchSelect value={businessId} onChange={(id) => { setBusinessId(id); setPage(1); }} />
        </div>
        <select value={direction} onChange={(e) => { setDirection(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Todas direcciones</option>
          <option value="push">Push</option>
          <option value="pull">Pull</option>
        </select>
        <input type="text" value={productCode} onChange={(e) => setProductCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
          placeholder="Producto code..."
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-40" />
        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        <button onClick={() => { setPage(1); load(); }} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm">
          <Search className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Negocio</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Producto</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Direcci&oacute;n</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Entidades</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Errores</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Sin resultados</td></tr>
              ) : data.items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{it.business_name}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{it.product_code || '\u2014'}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge
                      label={it.direction === 'push' ? 'Push' : 'Pull'}
                      color={it.direction === 'push' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-[300px] truncate">{renderCounts(it.entity_counts)}</td>
                  <td className="px-4 py-3 text-center">
                    {(it.errors_count || 0) > 0 ? (
                      <Badge label={`${it.errors_count}`} color="bg-red-100 text-red-700" />
                    ) : (
                      <span className="text-slate-400 text-xs">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(it.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page || page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </div>
  );
};

// ======================== INVENTORY ADJUSTMENTS TAB ========================

const InventoryAdjustmentsTab = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [businessId, setBusinessId] = useState('');
  const [adjType, setAdjType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (businessId) params.business_id = businessId;
      if (adjType) params.adjustment_type = adjType;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      const res = await getSyncInventoryAdjustments(params);
      setData(res.data);
    } catch { toast.error('Error cargando ajustes de inventario'); }
    finally { setLoading(false); }
  }, [page, businessId, adjType, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const typeColors = {
    absolute: 'bg-purple-100 text-purple-700',
    increment: 'bg-green-100 text-green-700',
    decrement: 'bg-red-100 text-red-700',
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-64">
          <BusinessSearchSelect value={businessId} onChange={(id) => { setBusinessId(id); setPage(1); }} />
        </div>
        <select value={adjType} onChange={(e) => { setAdjType(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Todos los tipos</option>
          <option value="absolute">Absoluto</option>
          <option value="increment">Incremento</option>
          <option value="decrement">Decremento</option>
        </select>
        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Negocio</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Producto</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">SKU</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Tipo</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Cantidad</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Raz&oacute;n</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Source</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Sin resultados</td></tr>
              ) : data.items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{it.business_name}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{it.product_name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">{it.product_sku}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge label={it.adjustment_type} color={typeColors[it.adjustment_type] || 'bg-slate-100 text-slate-600'} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{it.quantity}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{it.reason || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{it.source || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(it.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page || page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </div>
  );
};

// ======================== MAIN COMPONENT ========================

const SyncDashboard = () => {
  const [activeTab, setActiveTab] = useState('price-lists');

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Sync POS V2</h1>
          <p className="text-slate-500 text-sm mt-1">Datos de sincronizaci&oacute;n entre software enLocal y la nube</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'bg-teal-50 text-teal-700 border border-teal-200'
                    : 'text-slate-600 hover:bg-slate-50 border border-transparent'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'price-lists' && <PriceListsTab />}
        {activeTab === 'shifts' && <ShiftSummariesTab />}
        {activeTab === 'sync-log' && <SyncLogTab />}
        {activeTab === 'inventory' && <InventoryAdjustmentsTab />}
      </div>
    </DashboardLayout>
  );
};

export default SyncDashboard;
